import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Card } from '../../ui'
import { TaskInlineCreator } from './TaskInlineCreator.jsx'
import { TaskListHeader } from './TaskListHeader.jsx'
import { TaskRow } from './TaskRow.jsx'
import { useTaskList } from './hooks/useTaskList.js'
import { useTaskMutations } from './hooks/useTaskMutations.js'
import { loadSortPref, saveSortPref } from './utils/sortPersistence.js'

const DEFAULTS = { field: 'dueDate', direction: 'asc' }

/**
 * Shared TaskList — used both above the Notes section on the live canvas
 * and on the historical (completed/cancelled) session view.
 *
 * Live mode (`mode="live"`) fetches via tasksForPair, subscribes to the
 * three pair subscriptions, and exposes optimistic mutations. Snapshot
 * mode (`mode="snapshot"`) renders the supplied `snapshotTasks` prop with
 * every interaction disabled and zero network calls.
 *
 * @param {object} props
 * @param {string} props.reportId
 * @param {string} props.managerId
 * @param {string} props.currentUserId
 * @param {string} props.reportName
 * @param {string} props.managerName
 * @param {'live'|'snapshot'} props.mode
 * @param {Array} [props.snapshotTasks] - frozen task list for snapshot mode
 * @param {Array<string>} [props.statusFilter] - e.g. ['TODO', 'IN_PROGRESS']
 * @param {boolean} [props.softCloseFilter] - keep tasks visible (with
 *   strikethrough) after they close mid-session; only hide on next mount
 * @param {string} props.sortPersistenceKey - 'canvas' | 'task-page' | …
 * @param {{field: string, direction: string}} [props.defaultSort]
 * @param {Function} [props.onCreateClicked] - replaces the inline creator with a + add task button
 * @param {string} [props.emptyStateMessage]
 * @param {boolean} [props.selectionMode] - render a checkbox at the start of every row
 * @param {string[]} [props.selectedIds] - controlled selection set (used by the Tasks page)
 * @param {Function} [props.onToggleSelected] - called with a taskId when its checkbox is toggled
 */
const OPEN_STATUSES = new Set(['TODO', 'IN_PROGRESS'])

export function TaskList({
  reportId,
  managerId,
  currentUserId,
  reportName,
  managerName,
  mode = 'live',
  snapshotTasks,
  statusFilter,
  softCloseFilter = false,
  sortPersistenceKey,
  defaultSort = DEFAULTS,
  onCreateClicked,
  emptyStateMessage,
  selectionMode = false,
  selectedIds,
  onToggleSelected,
}) {
  const isSnapshot = mode === 'snapshot'
  const [sort, setSort] = useState(() =>
    loadSortPref(sortPersistenceKey ?? 'default', defaultSort),
  )
  const [errorMessage, setErrorMessage] = useState(null)
  const errorTimerRef = useRef(null)

  const showError = useCallback((err) => {
    const text = err?.message ? `Couldn't save: ${err.message}` : "Couldn't save — please retry"
    setErrorMessage(text)
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current)
    errorTimerRef.current = setTimeout(() => setErrorMessage(null), 4000)
  }, [])

  useEffect(() => () => {
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current)
  }, [])

  const list = useTaskList({
    reportId,
    managerId,
    mode,
    snapshotTasks,
    statusFilter,
    softCloseFilter,
    sortField: sort.field,
    sortDirection: sort.direction,
  })

  const mutations = useTaskMutations({
    reportId,
    managerId,
    currentUserId,
    list,
    onError: showError,
  })

  function setSortField(field) {
    const next = { field, direction: sort.direction }
    setSort(next)
    saveSortPref(sortPersistenceKey ?? 'default', next)
  }
  function toggleSortDirection() {
    const next = { field: sort.field, direction: sort.direction === 'asc' ? 'desc' : 'asc' }
    setSort(next)
    saveSortPref(sortPersistenceKey ?? 'default', next)
  }

  const showInlineCreator = !isSnapshot && !onCreateClicked
  const isEmpty = list.tasks.length === 0
  const emptyText =
    emptyStateMessage ??
    (isSnapshot
      ? 'No tasks were open at the end of this session'
      : 'No tasks yet · add your first one below')

  return (
    <Card
      data-testid="task-list"
      padding="sm"
      style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      <TaskListHeader
        tasks={list.tasks}
        sortField={sort.field}
        sortDirection={sort.direction}
        onSortFieldChange={setSortField}
        onSortDirectionToggle={toggleSortDirection}
        errorMessage={errorMessage}
        onCreateClicked={onCreateClicked}
      />

      {!isSnapshot && list.loading ? (
        <div
          role="status"
          aria-label="Loading tasks"
          style={{ padding: '12px 4px', color: 'var(--text-secondary, #888)', fontSize: 12 }}
        >
          Loading tasks…
        </div>
      ) : isEmpty ? (
        <div
          style={{
            padding: '14px 4px',
            color: 'var(--text-secondary, #888)',
            fontSize: 12,
            textAlign: 'center',
          }}
        >
          {emptyText}
        </div>
      ) : (
        <div role="list" style={{ display: 'flex', flexDirection: 'column' }}>
          {list.tasks.map((task) => (
            <TaskRow
              key={task.taskId}
              task={task}
              reportId={reportId}
              managerId={managerId}
              reportName={reportName}
              managerName={managerName}
              currentUserId={currentUserId}
              disabled={isSnapshot}
              closed={!isSnapshot && softCloseFilter && !OPEN_STATUSES.has(task.status)}
              selectionMode={selectionMode}
              selected={selectionMode && Array.isArray(selectedIds) && selectedIds.includes(task.taskId)}
              onToggleSelected={selectionMode && onToggleSelected ? () => onToggleSelected(task.taskId) : undefined}
              onCycleStatus={isSnapshot ? undefined : () => mutations.cycleStatus(task)}
              onCyclePriority={isSnapshot ? undefined : () => mutations.cyclePriority(task)}
              onCycleAssignee={isSnapshot ? undefined : () => mutations.cycleAssignee(task)}
              onSetDueDate={isSnapshot ? undefined : (dueDate) => mutations.updateTask(task.taskId, { dueDate })}
              onUpdateTitle={isSnapshot ? undefined : (title) => mutations.updateTask(task.taskId, { title })}
              onDelete={isSnapshot ? undefined : () => mutations.deleteTask(task.taskId)}
            />
          ))}
        </div>
      )}

      {showInlineCreator && <TaskInlineCreator onCreate={mutations.createTask} />}
    </Card>
  )
}
